import { Box } from '@mui/material';
import { Fragment, ReactElement } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PaloModal } from './palo-modal';
import { PaloShortcut, paloShortcutLabel, usePaloShortcut } from './palo-shortcuts';

const c = tokens.color;

export interface PaloShortcutOverlayProps {
    open: boolean;
    onClose: () => void;
    shortcuts: PaloShortcut[];
    title?: string;
}

const keyLabel = (shortcut: PaloShortcut): string =>
    paloShortcutLabel(shortcut.combo, shortcut.meta, shortcut.shift);

/**
 * What "?" shows: the keys that work here, right now.
 *
 * It reads the live registry rather than a written-down list, so a shortcut
 * that is not registered cannot appear — the overlay can only promise what the
 * mask actually answers to.
 */
export const PaloShortcutOverlay = ({ open, onClose, shortcuts, title }: PaloShortcutOverlayProps): ReactElement => {
    // Same shadowing as the palette: Esc closes this before it means anything else.
    usePaloShortcut(open ? { combo: 'Escape', label: 'Schließen', hidden: true, handler: onClose } : undefined);

    const groups = shortcuts.reduce<{ label: string; items: PaloShortcut[] }[]>((all, shortcut) => {
        const label = 'string' === typeof shortcut.group ? shortcut.group : 'Allgemein';
        const group = all.find(entry => entry.label === label);

        if (group) {
            group.items.push(shortcut);
        } else {
            all.push({ label, items: [shortcut] });
        }

        return all;
    }, []);

    return (
        <PaloModal isVisible={open} handleClose={onClose} title={title ?? 'Tastenkürzel'} width="sm" themeContent={true}>
            {0 === groups.length && (
                <Box sx={{ fontSize: '12px', color: c.ink3 }}>Auf dieser Maske gibt es keine Tastenkürzel.</Box>
            )}

            {groups.map(group => (
                <Fragment key={group.label}>
                    <Box
                        sx={{
                            fontSize: '10.5px',
                            fontWeight: 640,
                            letterSpacing: '.04em',
                            textTransform: 'uppercase',
                            color: c.ink3,
                            padding: '9px 0 4px',
                        }}
                    >
                        {group.label}
                    </Box>
                    {group.items.map(shortcut => (
                        <Box
                            key={keyLabel(shortcut) + String(shortcut.label)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '9px',
                                padding: '4px 0',
                                fontSize: '12px',
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 26,
                                    textAlign: 'center',
                                    padding: '1px 5px',
                                    borderRadius: tokens.radius.small + 'px',
                                    border: `1px solid ${c.lineHard}`,
                                    backgroundColor: c.surfaceAlt,
                                    fontFamily: tokens.font.mono,
                                    fontSize: '11px',
                                    color: c.ink,
                                }}
                            >
                                {keyLabel(shortcut)}
                            </Box>
                            <Box component="span" sx={{ color: c.ink2 }}>
                                {shortcut.label}
                            </Box>
                        </Box>
                    ))}
                </Fragment>
            ))}
        </PaloModal>
    );
};
